import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { entitlementsApi } from '@/api/entitlements';
import { paymentApi, type Plan } from '@/api/payment';
import { LogoMainHorizontal } from '@/components/brand';
import { ArrowRight, Card, Spinner } from '@/components/icons';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import { useAuth } from '@/context/AuthContext';
import { ENTITLEMENTS_QUERY_KEY, useEntitlements } from '@/hooks/use-entitlements';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import {
    clearPaidSignupIntent,
    clearSubscriptionCheckoutStarted,
    markPaidSignupIntent,
    markSignUpStarted,
    markSubscriptionCheckoutStarted,
} from '@/lib/analytics';
import {
    AuthConflictPage,
    BillingPage,
    DashboardPage,
    VerifyEmailPage,
    WelcomePage,
} from '@/pages/constants';
import { createPageUrl } from '@/utils';
import type { ReactNode } from 'react';

const PLAN_IDS = ['BASIC', 'INTERMEDIATE', 'PRO'] as const;
const CONFIRMED_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing']);
const SUBSCRIPTION_CONFIRMATION_RETRY_DELAY_MS = 2_000;
const SUBSCRIPTION_CONFIRMATION_MAX_ATTEMPTS = 5;

const PLAN_LABELS = {
    BASIC: 'Basic Plan',
    INTERMEDIATE: 'Intermediate Plan',
    PRO: 'Pro Plan',
} satisfies Record<(typeof PLAN_IDS)[number], string>;

type PlanId = (typeof PLAN_IDS)[number];

const parsePlanId = (value?: string): PlanId | null => {
    const normalized = value?.trim().toUpperCase();
    return PLAN_IDS.find((planId) => planId === normalized) ?? null;
};

const getErrorStatus = (error: unknown) => {
    if (!error || typeof error !== 'object') return undefined;
    return (error as { response?: { status?: number } }).response?.status;
};

export default function SubscribePage() {
    const { planId: planParam } = useParams();
    const selectedPlan = useMemo(() => parsePlanId(planParam), [planParam]);
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const { user, status, isLoading: isAuthLoading, login } = useAuth();
    const {
        subscription,
        isLoading: isSubscriptionLoading,
        refetch: refetchSubscription,
    } = useSubscriptionStatus({ enabled: Boolean(user) });
    const {
        entitlements,
        isLoading: isEntitlementsLoading,
        refetch: refetchEntitlements,
    } = useEntitlements({ enabled: Boolean(user) });
    const [isStartingCheckout, setIsStartingCheckout] = useState(false);
    const [isStartingTrial, setIsStartingTrial] = useState(false);
    const [confirmationAttempts, setConfirmationAttempts] = useState(0);
    const loginStartedRef = useRef(false);
    const checkoutStartedRef = useRef(false);

    const isCanceled = searchParams.get('canceled') === 'true';
    const isSuccess = searchParams.get('success') === 'true';
    const billingPath = createPageUrl(BillingPage);
    const welcomePath = createPageUrl(WelcomePage);
    const dashboardPath = createPageUrl(DashboardPage);

    const hasActiveSubscription = Boolean(
        subscription?.id && CONFIRMED_SUBSCRIPTION_STATUSES.has(subscription.status),
    );
    const hasConfirmedPaidEntitlements = Boolean(
        entitlements?.active &&
        entitlements.plan !== 'TRIAL' &&
        CONFIRMED_SUBSCRIPTION_STATUSES.has(entitlements.status),
    );

    useEffect(() => {
        if (isAuthLoading || status !== 'anonymous' || !selectedPlan) return;
        if (loginStartedRef.current) return;

        loginStartedRef.current = true;
        markSignUpStarted();
        markPaidSignupIntent(selectedPlan);

        void login({
            returnTo: location.pathname,
            authorizationParams: {
                screen_hint: 'signup',
            },
        });
    }, [isAuthLoading, location.pathname, login, selectedPlan, status]);

    useEffect(() => {
        if (status === 'pending_verification') {
            navigate(createPageUrl(VerifyEmailPage), { replace: true });
        }

        if (status === 'blocked_conflict') {
            navigate(createPageUrl(AuthConflictPage), { replace: true });
        }
    }, [navigate, status]);

    const startCheckout = useCallback(async () => {
        if (!selectedPlan) return;

        try {
            setIsStartingCheckout(true);
            const shouldDeferTrial = user?.welcome_screen_completed !== true;

            markSubscriptionCheckoutStarted({
                hadSubscription: Boolean(subscription?.plan),
            });

            const response = await paymentApi.checkout({
                planId: selectedPlan as Plan['id'],
                context: 'subscribe',
                trialBehavior: shouldDeferTrial ? 'defer_until_opt_in' : undefined,
            });

            window.location.href = response.url;
        } catch (error) {
            checkoutStartedRef.current = false;
            setIsStartingCheckout(false);

            if (getErrorStatus(error) === 409) {
                navigate(billingPath, { replace: true });
                return;
            }

            console.error('Checkout error:', error);
            toast.error('Não foi possível iniciar a assinatura agora. Tente novamente.');
        }
    }, [billingPath, navigate, selectedPlan, subscription?.plan, user?.welcome_screen_completed]);

    useEffect(() => {
        if (!user || !selectedPlan) return;
        if (isCanceled || isSuccess) return;
        if (isSubscriptionLoading) return;

        if (hasActiveSubscription) {
            navigate(billingPath, { replace: true });
            return;
        }

        if (checkoutStartedRef.current) return;
        checkoutStartedRef.current = true;
        void startCheckout();
    }, [
        billingPath,
        hasActiveSubscription,
        isCanceled,
        isSubscriptionLoading,
        isSuccess,
        navigate,
        selectedPlan,
        startCheckout,
        user,
    ]);

    useEffect(() => {
        if (!isSuccess || !user) return;

        if (subscription?.id && hasActiveSubscription && hasConfirmedPaidEntitlements) {
            // Emitted by the backend from the webhook reconcile. This screen and
            // Billing both used to fire it, so leaving either one in place would
            // count the sale twice.
            clearPaidSignupIntent();
            clearSubscriptionCheckoutStarted();
            navigate(
                user.welcome_screen_completed === true
                    ? `${billingPath}?success=true`
                    : welcomePath,
                { replace: true },
            );
            return;
        }

        if (isSubscriptionLoading || isEntitlementsLoading) return;
        if (confirmationAttempts >= SUBSCRIPTION_CONFIRMATION_MAX_ATTEMPTS) return;

        const retryTimer = window.setTimeout(() => {
            void refetchSubscription();
            void refetchEntitlements();
            setConfirmationAttempts((attempts) => attempts + 1);
        }, SUBSCRIPTION_CONFIRMATION_RETRY_DELAY_MS);

        return () => window.clearTimeout(retryTimer);
    }, [
        billingPath,
        confirmationAttempts,
        hasActiveSubscription,
        hasConfirmedPaidEntitlements,
        isEntitlementsLoading,
        isSubscriptionLoading,
        isSuccess,
        navigate,
        refetchEntitlements,
        refetchSubscription,
        subscription?.id,
        subscription?.plan,
        user,
        welcomePath,
    ]);

    const handleStartTrial = async () => {
        try {
            setIsStartingTrial(true);
            await entitlementsApi.optInTrial();
            clearPaidSignupIntent();
            clearSubscriptionCheckoutStarted();
            await queryClient.invalidateQueries({
                queryKey: ENTITLEMENTS_QUERY_KEY,
            });

            navigate(user?.welcome_screen_completed ? dashboardPath : welcomePath, {
                replace: true,
            });
        } catch (error) {
            console.error('Trial opt-in error:', error);
            toast.error('Não foi possível iniciar o teste grátis agora.');
            setIsStartingTrial(false);
        }
    };

    if (!selectedPlan) {
        return (
            <SubscribeShell>
                <h1 className="text-2xl font-semibold text-foreground">Plano indisponível</h1>
                <p className="mt-3 text-muted-foreground">
                    Não encontramos esse plano. Escolha uma opção disponível para continuar.
                </p>
                <Button className="mt-6" onClick={() => navigate(billingPath)}>
                    Ver planos
                </Button>
            </SubscribeShell>
        );
    }

    if (isAuthLoading || status === 'anonymous') {
        return <Loading />;
    }

    if (isSuccess) {
        return (
            <SubscribeShell>
                <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                    <Spinner className="size-6 animate-spin" />
                </div>
                <h1 className="mt-6 text-2xl font-semibold text-foreground">
                    Confirmando sua assinatura
                </h1>
                <p className="mt-3 text-muted-foreground">
                    Estamos sincronizando a confirmação da Stripe. Isso normalmente leva poucos
                    segundos.
                </p>
                {confirmationAttempts >= SUBSCRIPTION_CONFIRMATION_MAX_ATTEMPTS ? (
                    <Button className="mt-6" onClick={() => navigate(billingPath)}>
                        Ir para assinatura
                    </Button>
                ) : null}
            </SubscribeShell>
        );
    }

    if (isCanceled) {
        return (
            <SubscribeShell>
                <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                    <Card className="size-6" />
                </div>
                <h1 className="mt-6 text-2xl font-semibold text-foreground">Checkout cancelado</h1>
                <p className="mt-3 text-muted-foreground">
                    Para acessar o LaudoPsi, conclua a assinatura do plano{' '}
                    <strong>{PLAN_LABELS[selectedPlan]}</strong> ou escolha começar o teste grátis.
                </p>
                <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
                    <Button
                        onClick={startCheckout}
                        disabled={isStartingCheckout || isStartingTrial}
                    >
                        {isStartingCheckout ? <Spinner className="size-4 animate-spin" /> : null}
                        Concluir assinatura
                        {!isStartingCheckout ? <ArrowRight className="size-4" /> : null}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleStartTrial}
                        disabled={isStartingCheckout || isStartingTrial}
                    >
                        {isStartingTrial ? <Spinner className="size-4 animate-spin" /> : null}
                        Começar teste grátis
                    </Button>
                </div>
            </SubscribeShell>
        );
    }

    return (
        <SubscribeShell>
            <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                <Spinner className="size-6 animate-spin" />
            </div>
            <h1 className="mt-6 text-2xl font-semibold text-foreground">Preparando assinatura</h1>
            <p className="mt-3 text-muted-foreground">
                Você será direcionado para finalizar o plano{' '}
                <strong>{PLAN_LABELS[selectedPlan]}</strong> com segurança na Stripe.
            </p>
        </SubscribeShell>
    );
}

function SubscribeShell({ children }: { children: ReactNode }) {
    return (
        <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-6 py-16 text-center sm:px-8">
            <div aria-hidden="true" className="pointer-events-none absolute inset-0">
                <div className="absolute -left-56 -top-64 size-[44rem] rounded-full bg-brand-subtle/70 blur-3xl" />
                <div className="absolute -bottom-56 -right-48 size-[40rem] rounded-full bg-brand-subtle/70 blur-3xl" />
                <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-white/80" />
            </div>
            <section className="relative z-10 w-full max-w-lg rounded-4xl border border-border bg-card/90 p-8 shadow-elevation-3 backdrop-blur md:p-10">
                <div className="mb-8 flex justify-center">
                    <LogoMainHorizontal />
                </div>
                {children}
            </section>
        </main>
    );
}
